/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- Dialog owns keyboard navigation and its focus trap. */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  LuArrowLeft,
  LuArrowRight,
  LuCheck,
  LuX,
  LuStore,
  LuSettings,
  LuPalette,
  LuPackage,
  LuUsers,
  LuShoppingBag,
  LuLayoutDashboard,
} from "react-icons/lu";
import logo from "../../assets/logo/logo-delibran.svg";
import { guideSteps } from "./content";
const icons = {
  welcome: LuLayoutDashboard,
  settings: LuSettings,
  design: LuPalette,
  products: LuPackage,
  channels: LuStore,
  team: LuUsers,
  orders: LuShoppingBag,
};
export const OwnerGuideDialog = ({
  step,
  furthest = step,
  busy = false,
  error = "",
  onStep,
  onNext,
  onPause,
  onSection,
  onExitWithoutSave,
}) => {
  const root = useRef(null),
    heading = useRef(null),
    current = guideSteps[step],
    Icon = icons[current.icon];
  useEffect(() => {
    const previous = document.activeElement;
    heading.current?.focus();
    return () => {
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  useEffect(() => heading.current?.focus(), [step]);
  const keyboard = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      if (!busy) onPause();
    }
    if (e.key !== "Tab") return;
    const elements = [
      ...root.current.querySelectorAll(
        'button:not(:disabled),a[href],[tabindex="0"]',
      ),
    ].filter((el) => !el.matches(":disabled") && el.getClientRects().length);
    if (!elements.length) {
      e.preventDefault();
      return;
    }
    const first = elements[0],
      last = elements[elements.length - 1];
    if (
      e.shiftKey &&
      (document.activeElement === first ||
        document.activeElement === heading.current)
    ) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first?.focus();
    }
  };
  return createPortal(
    <div className="ownerGuideBackdrop">
      <button
        type="button"
        tabIndex={-1}
        className="ownerGuideOverlay"
        aria-label="Continuar la guía después"
        disabled={busy}
        onClick={onPause}
      />
      <section
        className="ownerGuideDialog"
        ref={root}
        role="dialog"
        aria-modal="true"
        aria-labelledby="owner-guide-title"
        aria-describedby="owner-guide-description"
        aria-busy={busy}
        onKeyDown={keyboard}
      >
        <aside className="ownerGuideSidebar">
          <img src={logo} alt="Delibrán" />
          <p>Tu guía de inicio</p>
          <ol aria-label="Pasos de la guía">
            {guideSteps.map((item, index) => (
              <li key={item.label}>
                <button
                  type="button"
                  disabled={busy || index > furthest}
                  aria-current={index === step ? "step" : undefined}
                  onClick={() => onStep(index)}
                >
                  <span>
                    {index < step ? <LuCheck aria-hidden="true" /> : index + 1}
                  </span>
                  {item.label}
                </button>
              </li>
            ))}
          </ol>
          <small>Puedes continuar a tu ritmo.</small>
        </aside>
        <div className="ownerGuideMain">
          <header className="ownerGuideTop">
            <span>
              Guía de inicio · {step + 1} de {guideSteps.length}
            </span>
            <button
              type="button"
              aria-label="Cerrar y continuar después"
              disabled={busy}
              onClick={onPause}
            >
              <LuX />
            </button>
          </header>
          <div className="ownerGuideBody">
            <div className="ownerGuideHeroIcon">
              <Icon aria-hidden="true" />
            </div>
            <p className="ownerGuideEyebrow">{current.eyebrow}</p>
            <h2 id="owner-guide-title" tabIndex={-1} ref={heading}>
              {current.title}
            </h2>
            <p id="owner-guide-description" className="ownerGuideLead">
              {current.description}
            </p>
            <div className="ownerGuideCards">
              {current.cards.map((card) => (
                <article key={card.title}>
                  <span>
                    <LuCheck aria-hidden="true" />
                  </span>
                  <div>
                    <h3>{card.title}</h3>
                    <p>{card.text}</p>
                  </div>
                </article>
              ))}
            </div>
            <p className="ownerGuideTip">{current.tip}</p>
            {current.path && (
              <button
                type="button"
                className="ownerGuideSectionLink"
                disabled={busy}
                onClick={() => onSection(current.path)}
              >
                {current.action}
                <LuArrowRight aria-hidden="true" />
              </button>
            )}
            {error && (
              <div className="ownerGuideError" role="alert">
                <p>{error}</p>
                <button
                  type="button"
                  disabled={busy}
                  onClick={onExitWithoutSave}
                >
                  Salir sin guardar el progreso
                </button>
              </div>
            )}
          </div>
          <footer>
            <div>
              {step > 0 && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onStep(step - 1)}
                >
                  <LuArrowLeft aria-hidden="true" />
                  Atrás
                </button>
              )}
              <button
                type="button"
                className="ownerGuideLater"
                disabled={busy}
                onClick={onPause}
              >
                Continuar después
              </button>
            </div>
            <button
              type="button"
              className="ownerGuidePrimary"
              disabled={busy}
              onClick={onNext}
            >
              {busy
                ? "Guardando…"
                : step === guideSteps.length - 1
                  ? "Finalizar guía"
                  : "Siguiente"}
              {!busy && <LuArrowRight aria-hidden="true" />}
            </button>
          </footer>
        </div>
      </section>
    </div>,
    document.body,
  );
};
